/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * EventShareMenu — the way an event leaves this page and reaches somebody else.
 *
 * A port of what the Android app does on the same screen: an icon over the
 * cover which hands "<title> — <origin>/p/event/<id>" to whatever the person
 * shares with. Android has one path for that, the system chooser. A browser
 * has two, and which one is available decides what this draws:
 *
 *   • `navigator.share` exists (every current phone browser) — the device's
 *     own share sheet opens, listing the apps THEY have rather than the four
 *     we happened to name. No menu is drawn at all.
 *   • It does not (most desktop browsers) — the apps that accept a message
 *     and a link are offered directly, with both filled in.
 *
 * THE TARGETS ARE NOT DEFINED HERE. `TARGETS` and `copyShareText` already
 * exist for certificate sharing and are not certificate-specific: they take a
 * message and a link and build an intent URL. Sharing is one behaviour in this
 * product, so a second table of WhatsApp and Telegram URLs would be a second
 * thing to keep correct.
 *
 * IT IS NOT INSIDE `.evb-hero`, and that is deliberate rather than tidy. The
 * hero sets `overflow:hidden` to crop the cover to its rounded corners, which
 * clips anything escaping its box — a menu opening below the button was cut
 * off at the image edge. Both the button and the menu are children of
 * `.evb-hero-wrap`, which shares the hero's geometry and does not clip.
 */

import React, { useEffect, useRef, useState } from 'react';
import { TARGETS, copyShareText } from '../certificateShare';

/**
 * The apps offered when the browser has no share sheet of its own.
 *
 * NO EMAIL: an event reaches people through the apps they talk in, and the
 * mailto: a browser opens lands in whatever client the machine happens to have
 * registered — often none at all.
 *
 * INSTAGRAM IS NOT LIKE THE OTHERS, and cannot be. WhatsApp, Telegram and X
 * all publish a URL that opens a composer with the message already in it.
 * Instagram publishes none — there is no web intent to post, and no way for a
 * website to put text into their app. So this row does the only honest thing:
 * it puts the message on the clipboard and opens Instagram, leaving one paste
 * to the person. The label says so rather than pretending otherwise.
 */
const EVENT_TARGETS = ['whatsapp', 'instagram', 'telegram', 'x', 'copy'];

/** Instagram's row, defined here because its behaviour is not a URL builder. */
const INSTAGRAM = { label: 'Instagram', href: 'https://www.instagram.com/' };

/**
 * Each app's own mark, from the Bootstrap Icons sheet index.html already
 * loads. `TARGETS` carries icon names too, but they are Material Symbols
 * chosen for the certificates screen — and that font is not loaded here, so
 * reading them would print the word "chat" where a logo belongs. A coloured
 * tile alone was not enough either: colour names the app only to somebody who
 * already knows which green is which.
 */
const SHARE_ICONS = {
  whatsapp: 'bi-whatsapp',
  instagram: 'bi-instagram',
  telegram: 'bi-telegram',
  x: 'bi-twitter-x',
  copy: 'bi-link-45deg',
};

/** A row's wording. Instagram is not in the shared TARGETS table. */
const labelFor = key => (key === 'instagram' ? INSTAGRAM.label : TARGETS[key].label);

/** True when this browser can open the device's share sheet for a link. */
function canShareLink() {
  try {
    return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  } catch (_) {
    return false;
  }
}

export default function EventShareMenu({ url, title, message = '' }) {
  const [open, setOpen] = useState(false);
  // Which row last reported a copy — Instagram copies too, and a tick on the
  // wrong row would say the wrong thing.
  const [copied, setCopied] = useState('');
  const wrap = useRef(null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // The button toggles the menu itself, so a press on it is not "outside" —
    // treating it as one would close and reopen in the same gesture.
    const onDown = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  // No link means no page to send anybody to. Drawing a control that cannot
  // work is worse than drawing nothing.
  if (!url) return null;

  // The invitation, not the title: see shareMessage.js. The title is still the
  // subject line where a target has one, and what the link preview shows.
  const text = message || title || '';
  const share = { text, link: url, title: title || '' };

  const press = async () => {
    if (!canShareLink()) { setOpen(o => !o); return; }
    try {
      await navigator.share({ title: text, text, url });
    } catch (err) {
      // AbortError is the person closing the sheet — not a failure, and not a
      // reason to then show them a menu they did not ask for.
      if (err && err.name === 'AbortError') return;
      setOpen(true);
    }
  };

  const openTarget = async (key) => {
    if (key === 'copy' || key === 'instagram') {
      const ok = await copyShareText(share);
      setCopied(ok ? key : '');
      clearTimeout(timer.current);
      // Instagram opens in a new tab REGARDLESS of whether the clipboard
      // worked: a browser that refuses the clipboard still leaves somebody
      // able to share, and the message is on screen behind them to retype.
      if (key === 'instagram') window.open(INSTAGRAM.href, '_blank', 'noopener,noreferrer');
      // Long enough to read, short enough that the menu is not left asserting
      // something about a clipboard that has since moved on.
      timer.current = setTimeout(() => { setCopied(''); setOpen(false); }, 1800);
      return;
    }
    window.open(TARGETS[key].url(share), '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  return (
    <div className="evb-share" ref={wrap}>
      <button
        type="button"
        className="evb-share__btn"
        aria-label="Share this event"
        aria-expanded={open}
        onClick={press}
      >
        {/* Inline rather than an icon font: nothing else on this page loads
            one, and a missing glyph would leave an empty circle over the
            cover. */}
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M18 16.08a2.9 2.9 0 0 0-1.96.77L8.91 12.7a3.2 3.2 0 0 0 0-1.4l7.05-4.11A2.99 2.99 0 1 0 15 5c0 .24.04.47.09.7L8.04 9.81a3 3 0 1 0 0 4.38l7.12 4.16c-.05.21-.08.43-.08.65a2.92 2.92 0 1 0 2.92-2.92Z" />
        </svg>
      </button>

      {open && (
        <div className="evb-share__menu" role="dialog" aria-label="Share this event">
          <b className="evb-share__h">Share this event</b>
          {EVENT_TARGETS.map(key => (
            <button
              key={key}
              type="button"
              className="evb-share__app"
              onClick={() => openTarget(key)}
            >
              <span className="evb-share__dot" data-arcee-share={key} aria-hidden="true">
                <i className={`bi ${SHARE_ICONS[key]}`} />
              </span>
              {copied === key
                ? (key === 'instagram' ? 'Copied — paste it there' : 'Link copied')
                : labelFor(key)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
